"use client";

import { zodPhoneField } from "./phone";
import { EventItem } from "@/models/event-item";
import moment from "moment";
import validator from "validator";
import { Collections } from "./collections";
import { z } from "zod";
import {
  AcceptTermsFormControl,
  CheckboxFormControl,
  CheckboxGroupFormControl,
  CheckboxWithNoteFormControl,
  FileFormControl,
  FormControl,
  InputGroupType,
  IntroFormControl,
  OptionWithNote,
  RadioFormControl,
  TextFormControl,
  TextLengthCheckOptionType,
} from "@/models/form-types";
import { ERROR_MESSAGES, defaultRadioOptions } from "./form-constants";
import { User } from "@/models/user";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";
import { db, storage } from "@/helpers/firebase/firebase";

import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import {
  collection,
  DocumentSnapshot,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import _ from "lodash";
import { CreateStripeCheckoutSessionParams } from "@/models/CreateStripeCheckoutSessionParams";
import Stripe from "stripe";
import { getCustomerIdFromUID } from "../server-actions/stripe-collections";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const MAX_FILE_SIZE_MB = 5;
const countWords = (text: string) => {
  return text.trim().split(/\s+/).length;
};

// Define URL validation options
const urlOptions: validator.IsURLOptions = {
  protocols: ["http", "https"],
  require_protocol: true,
};

export const zodTextarea = (
  label: string,
  isOptional: boolean = false,
  minLength?: number,
  maxLength?: number,
  criteria: TextLengthCheckOptionType = TextLengthCheckOptionType.CHAR_COUNT
) => {
  let schema = z.string().refine(
    (val) => {
      return isOptional || !!val?.trim();
    },
    { message: ERROR_MESSAGES.FieldRequired("Field") }
  );
  if (!!minLength || !!maxLength) {
    return schema
      .refine(
        (val) => {
          if (!val) return true;
          if (criteria === TextLengthCheckOptionType.CHAR_COUNT) {
            return !minLength || val.length >= minLength;
          }
          const wordCount = countWords(val);
          return !minLength || wordCount >= minLength;
        },
        {
          message: `${label} must be at more than ${minLength} ${
            criteria == TextLengthCheckOptionType.CHAR_COUNT
              ? "characters"
              : "words"
          }`,
        }
      )
      .refine(
        (val) => {
          if (!val) return true;
          if (criteria === TextLengthCheckOptionType.CHAR_COUNT) {
            return !maxLength || val.length <= maxLength;
          }
          const wordCount = countWords(val);
          return !maxLength || wordCount <= maxLength;
        },
        {
          message: `${label} must be at less than ${maxLength} ${
            criteria == TextLengthCheckOptionType.CHAR_COUNT
              ? "characters"
              : "words"
          }`,
        }
      );
  }
  return schema;
};

export const zodPhone = (label: string, isOptional: boolean = false) => {
  return zodPhoneField(isOptional);
};

export const zodCheckbox = (
  label: string,
  options: string[],
  isOptional: boolean = false
) => {
  let schema = z.array(z.enum(options as [string, ...string[]]));
  return isOptional
    ? schema.optional()
    : schema.min(1, { message: ERROR_MESSAGES.SelectOne() });
};

export const zodCheckboxGroup = (
  label: string,
  options: InputGroupType[],
  isOptional: boolean = false
) => {
  let schema = z.array(
    z.enum(options.flatMap((opt) => opt.options) as [string, ...string[]])
  );
  return isOptional
    ? schema.optional()
    : schema.min(1, { message: ERROR_MESSAGES.SelectOne() });
};

export const zodCheckboxWithNote = (
  label: string,
  options: OptionWithNote<string>[],
  isOptional: boolean = false
) => {
  let schema = z.array(
    z.enum(options.map((opt) => opt.opt) as [string, ...string[]])
  );
  return isOptional
    ? schema.optional()
    : schema.min(1, { message: ERROR_MESSAGES.SelectOne() });
};

export const zodRadio = (
  label: string,
  options: string[],
  isOptional: boolean = false
) => {
  let schema = z.enum(options as [string, ...string[]], {
    required_error: ERROR_MESSAGES.SelectOne(),
  });
  return isOptional ? schema.optional() : schema;
};

// People often type "www.brand.com" or "brand.com"; add https:// so it saves
// as a working link instead of failing validation.
export const normalizeUrlInput = (value: unknown) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed || /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) return trimmed;
  return /^[^\s@/]+\.[a-z]{2,}([/?#].*)?$/i.test(trimmed) ? `https://${trimmed}` : trimmed;
};

export const zodUrl = (label: string, isOptional: boolean = false) => {
  const schema = z.string().url({ message: ERROR_MESSAGES.ValidUrl() });
  const inner = isOptional
    ? schema.or(z.literal(""))
    : schema.min(1, { message: ERROR_MESSAGES.FieldRequired("Field") });
  // "url" lets the form show an example web address under the question.
  return z.preprocess(normalizeUrlInput, inner).describe("url");
};

export const zodString = (
  label: string,
  isOptional: boolean = false,
  minLength?: number,
  maxLength?: number,
  criteria: TextLengthCheckOptionType = TextLengthCheckOptionType.CHAR_COUNT
) => {
  return zodTextarea(label, isOptional, minLength, maxLength, criteria);
};

export const zodEmailValidator = (
  label: string,
  isOptional: boolean = false
) => {
  let schema = z.string().email({ message: "Invalid email address" });
  return isOptional
    ? schema.optional()
    : schema.min(1, { message: "Email is required" });
};

export const zodFileValidator = (
  fieldName: string,
  acceptedExtensions: string[],
  maxFileSizeMB: number = MAX_FILE_SIZE_MB,
  isOptional: boolean = false
) =>
  z
    .any()
    .refine((files) => _.isEmpty(files) || files.length > 0 || isOptional, {
      message: `${fieldName} is required`,
    })
    .refine(
      (files) => {
        if (!!files && files.length > 0) {
          if (typeof files === "string" && validator.isURL(files, urlOptions)) {
            return true;
          } else {
            const file = files[0];
            const extension = file.name.split(".").pop();
            const fileSizeMB = file.size / 1024 / 1024;
            return (
              acceptedExtensions.includes(extension) &&
              fileSizeMB <= maxFileSizeMB
            );
          }
        } else if (!!files && !_.isEmpty(files)) {
          const file = files;
          const extension = file.name.split(".").pop();
          const fileSizeMB = file.size / 1024 / 1024;
          return (
            acceptedExtensions.includes(extension) &&
            fileSizeMB <= maxFileSizeMB
          );
        }
        return true;
      },
      {
        message: `${fieldName} must be less than ${maxFileSizeMB}MB and of type ${acceptedExtensions.join(
          ", "
        )}`,
      }
    );

export const zodPhoneNumberValidator = zodPhoneField();

export const zodAcceptTerms = z
  .enum(defaultRadioOptions)
  .refine((val) => val == "Yes", { message: ERROR_MESSAGES.AcceptTerms() });

export const validatePhoneNumber = (phoneNumber: string) => {
  const number = phoneNumber.startsWith("+") ? phoneNumber : `+${phoneNumber}`;
  return validator.isMobilePhone(number, "any", {
    strictMode: true,
  });
};

export const validateEmail = (email: string) => {
  return validator.isEmail(email);
};

export const isEmpty = (str: string) => {
  return validator.isEmpty(str, { ignore_whitespace: true });
};

export const getIntialials = (
  firstname: string | undefined,
  lastname: string | undefined
): string => {
  return (
    (firstname?.charAt(0).toUpperCase() ?? "") +
    (lastname?.charAt(0).toUpperCase() ?? "")
  );
};

export function getValueFromFormData<T>(
  form: FormData,
  key: string
): T | undefined {
  const val = form.get(key);
  if (val) return val as T;
  return undefined;
}

export function getAllValueFromFormData<T>(
  form: FormData,
  key: string
): T | undefined {
  const val = form.getAll(key);
  if (val) return val as T;
  return undefined;
}

export function filterEventsByDate(
  eventSchedule: EventItem[],
  clickedDate: string
): EventItem[] {
  return eventSchedule.filter((event: EventItem) => {
    const eventDate = moment.unix(event.startTime.seconds).format("YYYY-MM-DD");
    return moment(eventDate).isSame(
      moment(clickedDate.trim(), "ddd MMM DD"),
      "day"
    );
  });
}

// File identity is stable while a form remains mounted; reuse successful
// uploads after a failed save, and discard rejected promises so retry works.
const formUploads = new WeakMap<File, Map<string, Promise<string>>>();

export function uploadFormFile(file: File, userId: string): Promise<string> {
  let uploads = formUploads.get(file);
  if (!uploads) {
    uploads = new Map();
    formUploads.set(file, uploads);
  }
  const existing = uploads.get(userId);
  if (existing) return existing;
  const upload = uploadToStorage({
    file,
    path: `${Collections.USERS}/${userId}/${Collections.FORM_RESPONSES}/${crypto.randomUUID()}-${file.name}`,
  }).catch((error) => {
    uploads!.delete(userId);
    throw error;
  });
  uploads.set(userId, upload);
  return upload;
}

export async function processFormResponse({
  formResponse,
  userId,
}: {
  formResponse: any;
  userId: string;
}) {
  for (const [key, value] of Object.entries(formResponse)) {
    if (typeof FileList !== "undefined" && value instanceof FileList) {
      if (value.length > 0) formResponse[key] = await uploadFormFile(value[0], userId);
      else delete formResponse[key];
    }
  }

  return formResponse;
}

export async function processMarketplaceResponse({
  formResponse,
  userId,
}: {
  formResponse: any;
  userId: string;
}) {
  for (const [key, value] of Object.entries(formResponse)) {
    if (value instanceof FileList && value.length > 0) {
      let fileName = `${new Date().getTime()}-${value[0].name}`;
      let url = await uploadToStorage({
        file: value[0],
        path: `${Collections.USERS}/${userId}/${Collections.MARKETPLACE}/${fileName}`,
      });
      formResponse[key] = url;
    } else if (key === "media" && value instanceof Array && value.length > 0) {
      let mediaUrls = [];
      for (let i = 0; i < value.length; i++) {
        if (typeof value[i] === "object") {
          let fileName = `${new Date().getTime()}-${value[i].name}`;
          let url = await uploadToStorage({
            file: value[i],
            path: `${Collections.USERS}/${userId}/${Collections.MARKETPLACE}/media/${fileName}`,
          });
          mediaUrls.push(url);
        } else {
          mediaUrls.push(value[i]);
        }
      }
      formResponse[key] = mediaUrls;
    } else if (
      key === "documents" &&
      value instanceof Array &&
      value.length > 0
    ) {
      let docUrls = [];
      for (let i = 0; i < value.length; i++) {
        if (typeof value[i] === "object") {
          let fileName = `${new Date().getTime()}-${value[i].name}`;
          let url = await uploadToStorage({
          file: value[i],
          path: `${Collections.USERS}/${userId}/${Collections.MARKETPLACE}/documents/${fileName}`,
          });
          docUrls.push(url);
        }
        else {
          docUrls.push(value[i]);
        }
         
      }
      formResponse[key] = docUrls;
    }
  }

  return formResponse;
}

export const getUniqueDates = (eventSchedule: EventItem[]) => {
  const uniqueDates = [
    ...new Set(
      eventSchedule.map((event) =>
        moment.unix(event.startTime.seconds).format("YYYY-MM-DD")
      )
    ),
  ];

  // Sort dates chronologically
  return uniqueDates.sort((a, b) => moment(a).diff(moment(b)));
};

export function filterByUniqueDate(eventSchedule: EventItem[], index: number) {
  // Get unique dates

  const uniqueDates = getUniqueDates(eventSchedule);

  // Get the date for the provided index
  const date = uniqueDates[index];

  // Format the date
  const formattedMonthDay = moment(date, "YYYY-MM-DD").format("MMM DD");
  const day = moment(date, "YYYY-MM-DD").format("ddd");
  const clickedDate = `${day}, ${formattedMonthDay}`;

  // Filter events based on the date
  return filterEventsByDate(eventSchedule, clickedDate);
}

export function labelToValue(label: string): string {
  // Replace all non-word characters with underscores
  // Convert to lowercase
  return label.toLowerCase().replace(/\W+/g, "_").replace(/_+/g, "_");
}
export const getFileType = (fileName: string) => {
  if (fileName && fileName.length > 0) {
    let isImage = false;
    const fileExtension = fileName.split(".").pop()?.toLowerCase();
    const imageExtensions = ["jpg", "jpeg", "png"];
    isImage = fileExtension ? imageExtensions.includes(fileExtension) : false;
    return isImage;
  }
};

export function createIntroControl(
  label: string,
  note: string[]
): IntroFormControl {
  return { label, type: "intro", notes: note };
}

export function createTextControl(
  label: string,
  note: string = "",
  isOptional: boolean = false,
  minLength?: number,
  maxLength?: number,
  criteria: TextLengthCheckOptionType = TextLengthCheckOptionType.CHAR_COUNT
): TextFormControl {
  return {
    label,
    type: "text",
    notes: [note],
    isOptional,
    minLength,
    maxLength,
    options: { lengthCheckType: criteria },
  };
}

export function createPhoneControl(
  label: string,
  note: string = "",
  isOptional: boolean = false
): FormControl {
  return {
    label,
    isOptional,
    type: "phone",
    notes: [note],
  };
}

export function createUrlControl(
  label: string,
  note: string = "",
  isOptional: boolean = false
): FormControl {
  return {
    label,
    type: "url",
    isOptional,
    notes: [note],
  };
}

export function createEmailControl(
  label: string,
  note: string = "",
  isOptional: boolean = false
): FormControl {
  return {
    label,
    type: "email",
    isOptional,
    notes: [note],
  };
}

export function createCheckboxControl<T extends readonly string[]>(
  label: string,
  options: T,
  note: string = "",
  isOptional: boolean = false
): CheckboxFormControl {
  return {
    label,
    type: "checkbox",
    isOptional,
    options: [...options] as string[],
    notes: [note],
  };
}

export function createCheckboxGroupControl<T extends InputGroupType[]>(
  label: string,
  options: T,
  note: string = ""
): CheckboxGroupFormControl {
  return {
    label,
    type: "checkbox-group",
    options: [...options],
    notes: [note],
  };
}

export function createRadioControl<T extends readonly string[]>(
  label: string,
  options: T,
  note: string = "",
  isOptional: boolean = false
): RadioFormControl {
  return {
    label,
    type: "radio",
    isOptional,
    options: [...options] as string[],
    notes: [note],
  };
}

export function createTextareaControl(
  label: string,
  note: string = "",
  isOptional: boolean = false,
  minLength?: number,
  maxLength?: number,
  criteria: TextLengthCheckOptionType = TextLengthCheckOptionType.CHAR_COUNT
): TextFormControl {
  return {
    label,
    type: "textarea",
    notes: [note],
    isOptional,
    minLength,
    maxLength,
    options: { lengthCheckType: criteria },
  };
}

export function createFileControl(
  label: string,
  note: string,
  maxFileSizeMB: number = 5,
  allowedFileExtentions: string[],
  isOptional: boolean = false
): FileFormControl {
  return {
    label,
    type: "file",
    notes: [note],
    isOptional,
    options: {
      maxFileSizeMB,
      allowedFileExtentions,
    },
  };
}

export function createCheckboxWithNoteControl<T extends string>(
  label: string,
  options: OptionWithNote<T>[],
  note: string = ""
) {
  return {
    label: label,
    notes: [note],
    type: "checkbox-with-note",
    options: options,
  } as CheckboxWithNoteFormControl<T>;
}

export function createAcceptControl(
  termsAndconditions?: string
): AcceptTermsFormControl {
  return { label: "", type: "accept", termsAndconditions: termsAndconditions };
}

export const formatLowercaseUnderscore = (value: string) => {
  let space_underscore = value.split(" ").join("_");
  return space_underscore.charAt(0).toLowerCase() + space_underscore.slice(1);
};

export const calculateProfilePercentage = (userInfo: User) => {
  let percentage = 33;
  if (userInfo && userInfo.publicProfileInfo) {
    percentage += 33;
  }
  if (userInfo && userInfo.careerDemographicsInfo) {
    percentage += 33;
  }
  if (userInfo && userInfo.photoUrl) {
    percentage += 1;
  }

  return percentage;
};

export async function uploadToStorage({
  file,
  path,
}: {
  file: File | Blob;
  path: string;
}) {
  // upload to storage
  const storageRef = ref(storage, path);
  const result = await uploadBytes(storageRef, file);
  return await getDownloadURL(result.ref);
}

export async function deleteFromStorage(path: string) {
  try {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
    return true;
  } catch (e) {
    return false;
  }
}

const FORM_ID_YEAR_SUFFIX_REGEX = /_(20\d{2})$/;

export function getBaseFormDocumentId(formId: string) {
  return formId.replace(FORM_ID_YEAR_SUFFIX_REGEX, "");
}

export function getFestivalYearFromFormDocumentId(formId: string) {
  const match = formId.match(FORM_ID_YEAR_SUFFIX_REGEX);
  return match ? Number(match[1]) : undefined;
}

export function getSlugFromFormId(formId: string) {
  return getBaseFormDocumentId(formId).replaceAll("_", "/");
}

export function getFormIdFromSlug(slug: string) {
  // replace "/" with "_"
  return slug.replaceAll("/", "_");
}

export function defaultMap<T>(doc: DocumentSnapshot) {
  return { ...doc.data(), id: doc.id } as T;
}

export async function deleteFirestoreCollection(path: string) {
  const collectionRef = collection(db, path);
  const snapshot = await getDocs(collectionRef);
  if (snapshot.empty) {
    return;
  }
  const batch = writeBatch(db);
  for (const doc of snapshot.docs) {
    batch.delete(doc.ref);
  }
  await batch.commit();
}

export const downloadImage = (url?: string) => {
  if (!url) return;
  fetch(url)
    .then((response) => response.blob())
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = "image.jpg";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    })
    .catch(() => alert("Failed to download image."));
};

export const extractFileName = (url: string) => {
  // Decode the URL to convert %20 and other encoded characters to their actual form
  const decodedUrl = decodeURIComponent(url);

  // Use a regular expression to extract the file name after the last slash
  const fileNameMatch = decodedUrl.match(/\/([^\/?#]+)\?alt=media/);

  // If a match is found, return the file name, otherwise return null
  return fileNameMatch ? fileNameMatch[1] : null;
};

export const createStripeCheckoutSession = async (
  createStripeCheckoutSessionParams: CreateStripeCheckoutSessionParams
) => {
  const customer = await getCustomerIdFromUID(
    createStripeCheckoutSessionParams.uid
  );
  let stripe: Stripe;
  const apiKey = process.env.STRIPE_SECRET_KEY;
  if (!apiKey || !customer) return null;

  stripe = new Stripe(apiKey, { apiVersion: '2026-07-29.dahlia' });
  const session = await stripe.checkout.sessions.create({
    payment_method_types: ["card"],
    customer: customer,
    line_items: createStripeCheckoutSessionParams.lineItems.map((item) => ({
      ...item,
      adjustable_quantity: { enabled: false },
    })),
    mode: "payment",
    cancel_url: createStripeCheckoutSessionParams.cancelUrl,
    success_url: createStripeCheckoutSessionParams.successUrl,
    metadata: {
      payment_type: createStripeCheckoutSessionParams.metadata.payment_type,
      booking_info: JSON.stringify(
        createStripeCheckoutSessionParams.metadata.booking_info
      ),
    },
  });

  return session;
};

export const extractLabels = (schema: {
  [nestedKey: string]: FormControl;
}): {
  [key: string]: string;
} => {
  const labels: { [key: string]: string } = {};
  for (const key in schema) {
    if (schema[key] && schema[key].label) {
      labels[key] = schema[key].label;
    }
  }
  return labels;
};

export const extractHeaders = (schema: {
  [nestedKey: string]: FormControl;
}): string[] => {
  return Object.keys(schema);
};

/**
 * Find a Pass definition that matches a user's purchased pass.
 * Handles both legacy passes (from 'passes' collection) and new ticketTypes.
 * Uses multiple matching strategies for robustness.
 */
export function findPassForUserPass<
  TUserPass extends {
    passId?: string;
    ticketTypeSlug?: string;
    productId?: string;
    item?: { name?: string };
  },
  TPass extends {
    id?: string;
    slug?: string;
    title?: string;
    displayName?: string;
    productId?: string;
  }
>(userPass: TUserPass, passes: TPass[]): TPass | undefined {
  // Normalize item name for comparison (e.g., "tribe-pass" -> "tribe")
  const itemNameLower = userPass.item?.name?.toLowerCase() || "";
  const itemSlug = itemNameLower
    .replace(/-pass$/, "")
    .replace(/\s+pass$/i, "")
    .trim();

  return passes.find((p) => {
    // Direct ID matches
    if (p.id && userPass.passId && p.id === userPass.passId) return true;
    if (p.id && userPass.ticketTypeSlug && p.id === userPass.ticketTypeSlug)
      return true;

    // Product-based matching (Stripe product IDs)
    if (p.productId && userPass.productId && p.productId === userPass.productId)
      return true;

    // Slug-based matching (most reliable for new system)
    if (p.slug && userPass.ticketTypeSlug && p.slug === userPass.ticketTypeSlug)
      return true;
    if (p.slug && itemSlug && p.slug === itemSlug) return true;

    // Title/displayName matching (case-insensitive)
    const pTitleLower = p.title?.toLowerCase() || "";
    const pDisplayLower = p.displayName?.toLowerCase() || "";
    if (itemNameLower && pTitleLower === itemNameLower) return true;
    if (itemNameLower && pDisplayLower === itemNameLower) return true;

    // Partial match: pass title contains item slug
    if (itemSlug && pTitleLower.includes(itemSlug)) return true;

    return false;
  });
}
